import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedState === "all") {
      setFilteredLocations(locations);
    } else {
      setFilteredLocations(locations.filter((l) => l.state === selectedState));
    }
  }, [selectedState, locations]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
      setFilteredLocations(response.data);
      const uniqueStates = [...new Set(response.data.map((l) => l.state))];
      setStates(uniqueStates);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
    setLoading(false);
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="bg-[#14213D] py-20" data-testid="locations-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-1.5 bg-[#00B4D8]/20 text-[#00B4D8] text-sm font-medium rounded-full mb-6">
              Our Network
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Locations Across America
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Find an Medical Access Inc facility near you. Our network spans major cities 
              throughout the United States.
            </p>
          </motion.div>

          {/* US Map Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="relative bg-white/5 rounded-2xl p-8 overflow-hidden"
          >
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
              {locations.slice(0, 6).map((location, i) => (
                <div
                  key={location.id}
                  className="bg-white/10 rounded-lg p-4"
                >
                  <div className="w-8 h-8 bg-[#00B4D8] rounded-full flex items-center justify-center mx-auto mb-2">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-white text-sm font-medium">{location.city}</p>
                  <p className="text-gray-400 text-xs">{location.state}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter & Results */}
      <section className="py-16 bg-[#F1FAEE]" data-testid="locations-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-[#14213D]">{filteredLocations.length}</span> locations
            </p>
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger className="w-48 bg-white rounded-lg" data-testid="state-filter">
                <SelectValue placeholder="Filter by State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All States</SelectItem>
                {states.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Locations Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl h-64 animate-pulse" />
              ))}
            </div>
          ) : filteredLocations.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLocations.map((location, index) => (
                <motion.div
                  key={location.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden card-hover"
                  data-testid={`location-${location.id}`}
                >
                  <div className="h-32 bg-[#14213D] relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Building2 className="w-16 h-16 text-white/20" />
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <h3 className="font-heading font-semibold text-lg text-white">
                        {location.name}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#00B4D8] mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-gray-600">
                        {location.address}<br />
                        {location.city}, {location.state} {location.zip_code}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                      <a href={`tel:${location.phone}`} className="text-sm text-gray-600 hover:text-[#00B4D8]">
                        {location.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-[#00B4D8] flex-shrink-0" />
                      <span className="text-sm text-gray-600">{location.hours}</span>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-xs text-gray-500 mb-2">Services Available:</p>
                      <div className="flex flex-wrap gap-2">
                        {location.services?.slice(0, 3).map((service) => (
                          <span
                            key={service}
                            className="px-2 py-1 bg-[#F1FAEE] text-[#14213D] text-xs rounded"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl">
              <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-xl text-[#14213D] mb-2">
                No locations found
              </h3>
              <p className="text-gray-500">
                Try selecting a different state.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#00B4D8]" data-testid="locations-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Can't Find a Location Near You?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              We're continuously expanding our network. Contact us to learn about 
              upcoming locations or explore our telehealth options.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button
                  className="bg-white text-[#00B4D8] rounded-lg px-6 py-5 hover:bg-gray-100"
                  data-testid="contact-us"
                >
                  Contact Us
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="border-white text-white rounded-lg px-6 py-5 hover:bg-white/10"
                  data-testid="telehealth-services"
                >
                  Telehealth Services
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
